import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, Minus } from "lucide-react";
import {
  getActivityTypePillToken,
  getClinicianRolePillToken,
  getValuePillToken,
} from "../config/palette";

export function StatusPill({ status }: { status: string }) {
  const normalized = status.trim().toLowerCase();
  const tone =
    normalized === "activated" ||
    normalized === "active" ||
    normalized === "enabled" ||
    normalized === "on"
      ? "active"
      : "neutral";
  return (
    <span className="cp-status-pill" data-tone={tone}>
      {status || "Not provided"}
    </span>
  );
}

export function ActivityTemplateStatusPill({ status }: { status: string }) {
  const displayStatus = status.trim();
  if (!displayStatus) return <span className="cp-empty-value">—</span>;

  const normalized = displayStatus.toLocaleLowerCase();
  const label =
    normalized === "on" ? "On" : normalized === "off" ? "Off" : displayStatus;

  return (
    <span
      className="cp-status-pill"
      data-tone={normalized === "on" ? "active" : "neutral"}
    >
      {label}
    </span>
  );
}

export function ActivityTypePill({
  activityType,
  colorKey,
  title,
}: {
  activityType: string;
  colorKey?: string;
  title?: string;
}) {
  if (!activityType) return <span className="cp-empty-value">—</span>;
  return (
    <span
      className="cp-value-pill cp-activity-type-pill"
      data-palette={getActivityTypePillToken(activityType, colorKey)}
      title={title}
    >
      {activityType}
    </span>
  );
}

export function ClinicianRolePill({ role }: { role: string }) {
  const displayRole = role.trim();
  if (!displayRole) return <span className="cp-empty-value">—</span>;

  const label = displayRole
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toLocaleUpperCase());

  return (
    <span
      className="cp-value-pill cp-clinician-role-pill"
      data-palette={getClinicianRolePillToken(displayRole)}
    >
      {label}
    </span>
  );
}

export function ClientValuePill({ value }: { value?: string }) {
  const displayValue = value?.trim();
  if (!displayValue) return <span className="cp-empty-value">—</span>;

  return (
    <span className="cp-value-pill" data-palette="greenLight1">
      {displayValue}
    </span>
  );
}

export function HomeworkStatusPill({ status }: { status: string }) {
  const tone =
    status === "Completed"
      ? "completed"
      : status === "Completed with Modification"
        ? "modified"
        : status === "Attempted"
          ? "attempted"
          : status === "Missed"
            ? "missed"
            : "neutral";

  return (
    <span className="cp-homework-status-pill" data-tone={tone}>
      {status || "Not provided"}
    </span>
  );
}

export function ExpectancyResultPill({ result }: { result: string }) {
  const tone =
    result === "Proved false"
      ? "info"
      : result === "Came true"
        ? "teal"
        : result === "Partly true"
          ? "success"
          : result === "Pending"
            ? "warning"
            : "neutral";

  return (
    <span className="cp-expectancy-result-pill" data-tone={tone}>
      {result || "—"}
    </span>
  );
}

export function SudsPill({
  value,
  emptyVariant = "pill",
  emptyLabel = "No SUDS rating",
}: {
  value: number | null;
  emptyVariant?: "pill" | "circle";
  emptyLabel?: string;
}) {
  if (value === null && emptyVariant === "circle") {
    return (
      <span className="cp-suds-empty-circle">
        <span className="cp-visually-hidden">{emptyLabel}</span>
      </span>
    );
  }

  const tone =
    value === null || value < 0 || value > 10
      ? "neutral"
      : value <= 3
        ? "low"
        : value <= 6
          ? "medium"
          : value <= 8
            ? "high"
            : "very-high";
  return (
    <span className="cp-suds-pill" data-tone={tone}>
      {value === null ? "—" : value}
    </span>
  );
}

export function BooleanIndicator({
  value,
  trueLabel = "Yes",
  falseLabel = "No",
  trueTone = "success",
}: {
  value: boolean;
  trueLabel?: string;
  falseLabel?: string;
  trueTone?: "success" | "danger";
}) {
  return (
    <span className="cp-boolean" data-value={value} data-tone={trueTone}>
      <span className="cp-boolean__icon" aria-hidden="true">
        {value ? <Check /> : <Minus />}
      </span>
      <span className="cp-visually-hidden">
        {value ? trueLabel : falseLabel}
      </span>
    </span>
  );
}

export function ValuePillList({
  values,
  kind,
  emptyLabel = "None",
}: {
  values: readonly string[];
  kind: "coping" | "fun" | "badge";
  emptyLabel?: string;
}) {
  if (values.length === 0)
    return <span className="cp-empty-value">{emptyLabel}</span>;
  return (
    <div className="cp-pill-list">
      {values.map((value, index) => (
        <span
          className="cp-value-pill"
          data-palette={getValuePillToken(value, kind)}
          key={`${value}-${index}`}
        >
          {value}
        </span>
      ))}
    </div>
  );
}

export function RelationshipPillList({ names }: { names: readonly string[] }) {
  if (names.length === 0) return <span className="cp-empty-value">—</span>;

  return (
    <div className="cp-pill-list cp-relationship-pill-list">
      {names.map((name) => (
        <span className="cp-value-pill cp-relationship-pill" key={name}>
          {name}
        </span>
      ))}
    </div>
  );
}

export function DisplayValue({ value }: { value: string | number | null }) {
  if (value === null || value === "") {
    return <span className="cp-empty-value">Not provided</span>;
  }
  return <>{value}</>;
}

export function ActionsColumnHeader() {
  return <span className="cp-visually-hidden">Actions</span>;
}

export type PortalTableSortDirection = "ascending" | "descending" | "none";

export interface PortalTableColumn<TRecord> {
  key: string;
  header: React.ReactNode;
  headerLabel?: string;
  initialWidth: number;
  minWidth?: number;
  resizable?: boolean;
  sortDirection?: PortalTableSortDirection;
  onSort?: () => void;
  cellClassName?: string;
  sticky?: "right";
  render: (record: TRecord) => React.ReactNode;
}

const DEFAULT_MIN_COLUMN_WIDTH = 80;
const MAX_COLUMN_WIDTH = 1200;
const KEYBOARD_RESIZE_STEP = 10;

function getColumnHeaderLabel<TRecord>(
  column: PortalTableColumn<TRecord>,
): string {
  if (column.headerLabel) return column.headerLabel;
  return typeof column.header === "string" ? column.header : column.key;
}

function getMinimumColumnWidth<TRecord>(
  column: PortalTableColumn<TRecord>,
): number {
  return column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH;
}

function getInitialColumnWidths<TRecord>(
  columns: readonly PortalTableColumn<TRecord>[],
): Record<string, number> {
  return Object.fromEntries(
    columns.map((column) => [
      column.key,
      Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(getMinimumColumnWidth(column), column.initialWidth),
      ),
    ]),
  );
}

export function PortalDataTable<TRecord>({
  columns,
  records,
  getRecordKey,
  caption,
  onRecordOpen,
  getRecordOpenLabel,
  isRecordOpenable,
  isRecordHighlighted,
  density = "default",
  preferredColumnWidths,
  onColumnWidthsChange,
}: {
  columns: readonly PortalTableColumn<TRecord>[];
  records: readonly TRecord[];
  getRecordKey: (record: TRecord) => string;
  caption: string;
  onRecordOpen?: (record: TRecord) => void;
  getRecordOpenLabel?: (record: TRecord) => string;
  isRecordOpenable?: (record: TRecord) => boolean;
  isRecordHighlighted?: (record: TRecord) => boolean;
  density?: "default" | "compact";
  preferredColumnWidths?: Readonly<Record<string, number>>;
  onColumnWidthsChange?: (widths: Record<string, number>) => void;
}) {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() =>
    getInitialColumnWidths(columns),
  );
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);
  const resizeStateRef = useRef<{
    columnKey: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  const bodyStyleRef = useRef<{ cursor: string; userSelect: string } | null>(
    null,
  );
  const minimumWidthsRef = useRef<Record<string, number>>({});

  minimumWidthsRef.current = Object.fromEntries(
    columns.map((column) => [column.key, getMinimumColumnWidth(column)]),
  );

  useEffect(() => {
    setColumnWidths((currentWidths) => {
      const nextWidths = getInitialColumnWidths(columns);
      let changed = Object.keys(currentWidths).length !== columns.length;

      for (const column of columns) {
        if (typeof currentWidths[column.key] === "number") {
          nextWidths[column.key] = Math.min(
            MAX_COLUMN_WIDTH,
            Math.max(
              getMinimumColumnWidth(column),
              currentWidths[column.key],
            ),
          );
        } else {
          changed = true;
        }

        if (nextWidths[column.key] !== currentWidths[column.key])
          changed = true;
      }

      return changed ? nextWidths : currentWidths;
    });
  }, [columns]);

  useEffect(() => {
    if (!preferredColumnWidths) return;
    setColumnWidths((currentWidths) => {
      let changed = false;
      const nextWidths = { ...currentWidths };
      for (const column of columns) {
        const preferredWidth = preferredColumnWidths[column.key];
        const nextWidth = Math.min(
          MAX_COLUMN_WIDTH,
          Math.max(
            getMinimumColumnWidth(column),
            Math.round(
              typeof preferredWidth === "number"
                ? preferredWidth
                : column.initialWidth,
            ),
          ),
        );
        if (nextWidths[column.key] !== nextWidth) {
          nextWidths[column.key] = nextWidth;
          changed = true;
        }
      }
      return changed ? nextWidths : currentWidths;
    });
  }, [columns, preferredColumnWidths]);

  useEffect(() => {
    const restoreBodyStyles = () => {
      if (!bodyStyleRef.current) return;
      document.body.style.cursor = bodyStyleRef.current.cursor;
      document.body.style.userSelect = bodyStyleRef.current.userSelect;
      bodyStyleRef.current = null;
    };

    const finishResize = () => {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      setResizingColumn(null);
      restoreBodyStyles();
    };

    const handlePointerMove = (event: PointerEvent) => {
      const resizeState = resizeStateRef.current;
      if (!resizeState) return;

      event.preventDefault();
      const minimumWidth =
        minimumWidthsRef.current[resizeState.columnKey] ??
        DEFAULT_MIN_COLUMN_WIDTH;
      const nextWidth = Math.min(
        MAX_COLUMN_WIDTH,
        Math.max(
          minimumWidth,
          Math.round(
            resizeState.startWidth + event.clientX - resizeState.startX,
          ),
        ),
      );

      setColumnWidths((currentWidths) => {
        const nextWidths = {
          ...currentWidths,
          [resizeState.columnKey]: nextWidth,
        };
        onColumnWidthsChange?.({ [resizeState.columnKey]: nextWidth });
        return nextWidths;
      });
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", finishResize);
    window.addEventListener("pointercancel", finishResize);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", finishResize);
      window.removeEventListener("pointercancel", finishResize);
      resizeStateRef.current = null;
      restoreBodyStyles();
    };
  }, [onColumnWidthsChange]);

  const getColumnWidth = (column: PortalTableColumn<TRecord>) =>
    columnWidths[column.key] ??
    Math.min(
      MAX_COLUMN_WIDTH,
      Math.max(getMinimumColumnWidth(column), column.initialWidth),
    );

  const tableWidth = useMemo(
    () =>
      columns.reduce(
        (total, column) =>
          total +
          (columnWidths[column.key] ??
            Math.min(
              MAX_COLUMN_WIDTH,
              Math.max(getMinimumColumnWidth(column), column.initialWidth),
            )),
        0,
      ),
    [columns, columnWidths],
  );

  const startColumnResize = (
    event: React.PointerEvent<HTMLDivElement>,
    column: PortalTableColumn<TRecord>,
  ) => {
    if (event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();

    const measuredWidth =
      event.currentTarget.closest("th")?.getBoundingClientRect().width ?? 0;
    const startWidth =
      measuredWidth > 0 ? measuredWidth : getColumnWidth(column);
    if (typeof startWidth !== "number") return;

    resizeStateRef.current = {
      columnKey: column.key,
      startX: event.clientX,
      startWidth,
    };
    setResizingColumn(column.key);

    if (!bodyStyleRef.current) {
      bodyStyleRef.current = {
        cursor: document.body.style.cursor,
        userSelect: document.body.style.userSelect,
      };
    }
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const resizeColumnWithKeyboard = (
    event: React.KeyboardEvent<HTMLDivElement>,
    column: PortalTableColumn<TRecord>,
  ) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    event.stopPropagation();
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const minimumWidth = getMinimumColumnWidth(column);

    setColumnWidths((currentWidths) => {
      const nextWidths = {
        ...currentWidths,
        [column.key]: Math.min(
          MAX_COLUMN_WIDTH,
          Math.max(
            minimumWidth,
            (currentWidths[column.key] ??
              Math.max(minimumWidth, column.initialWidth)) +
              direction * KEYBOARD_RESIZE_STEP,
          ),
        ),
      };
      onColumnWidthsChange?.({ [column.key]: nextWidths[column.key] });
      return nextWidths;
    });
  };

  const eventStartedFromInteractiveControl = (
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => {
    const target = event.target;
    return (
      target instanceof Element &&
      Boolean(
        target.closest(
          'a, button, input, select, textarea, summary, [role="button"], [role="menuitem"], [contenteditable="true"]',
        ),
      )
    );
  };

  return (
    <div className="cp-table-wrap">
      <table
        className="cp-data-table"
        data-density={density}
        style={{ width: `${tableWidth}px`, minWidth: "100%" }}
      >
        <caption className="cp-visually-hidden">{caption}</caption>
        <colgroup>
          {columns.map((column) => (
            <col
              key={column.key}
              style={{ width: `${getColumnWidth(column)}px` }}
            />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => {
              const sortDirection = column.sortDirection ?? "none";
              const headerLabel = getColumnHeaderLabel(column);
              return (
                <th
                  scope="col"
                  key={column.key}
                  aria-sort={column.onSort ? sortDirection : undefined}
                  data-sticky={column.sticky}
                >
                  {column.onSort ? (
                    <button
                      type="button"
                      className="cp-table-sort-button"
                      title={`Sort by ${headerLabel}`}
                      onClick={column.onSort}
                    >
                      <span>{column.header}</span>
                      {sortDirection === "ascending" ? (
                        <ArrowUp aria-hidden="true" />
                      ) : sortDirection === "descending" ? (
                        <ArrowDown aria-hidden="true" />
                      ) : (
                        <ArrowUpDown aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                  {column.resizable !== false ? (
                    <div
                      className="cp-column-resize-handle"
                      data-active={resizingColumn === column.key}
                      role="separator"
                      aria-label={`Resize ${getColumnHeaderLabel(column)} column`}
                      aria-orientation="vertical"
                      aria-valuemin={getMinimumColumnWidth(column)}
                      aria-valuemax={MAX_COLUMN_WIDTH}
                      aria-valuenow={Math.round(getColumnWidth(column))}
                      tabIndex={0}
                      onPointerDown={(event) =>
                        startColumnResize(event, column)
                      }
                      onKeyDown={(event) =>
                        resizeColumnWithKeyboard(event, column)
                      }
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    />
                  ) : null}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {records.map((record) => {
            const canOpen = Boolean(
              onRecordOpen && (isRecordOpenable?.(record) ?? true),
            );
            const openLabel = canOpen
              ? (getRecordOpenLabel?.(record) ?? "Open record")
              : undefined;

            return (
              <tr
                key={getRecordKey(record)}
                data-openable={canOpen || undefined}
                data-highlighted={isRecordHighlighted?.(record) || undefined}
                tabIndex={canOpen ? 0 : undefined}
                aria-label={openLabel}
                onClick={(event) => {
                  if (
                    !canOpen ||
                    event.defaultPrevented ||
                    eventStartedFromInteractiveControl(event)
                  ) {
                    return;
                  }
                  onRecordOpen?.(record);
                }}
                onKeyDown={(event) => {
                  if (
                    !canOpen ||
                    event.target !== event.currentTarget ||
                    (event.key !== "Enter" && event.key !== " ")
                  ) {
                    return;
                  }
                  event.preventDefault();
                  onRecordOpen?.(record);
                }}
              >
                {columns.map((column) => (
                  <td
                    className={column.cellClassName}
                    data-sticky={column.sticky}
                    key={column.key}
                  >
                    {column.render(record)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function ContentSection({
  title,
  actions,
  hideTitle = false,
  children,
}: {
  title: string;
  actions?: React.ReactNode;
  hideTitle?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="cp-content-section">
      {hideTitle && actions ? (
        <div className="cp-content-section__header cp-content-section__header--actions-only">
          <h2 className="cp-visually-hidden">{title}</h2>
          {actions}
        </div>
      ) : actions ? (
        <div className="cp-content-section__header">
          <h2>{title}</h2>
          {actions}
        </div>
      ) : !hideTitle ? (
        <h2>{title}</h2>
      ) : (
        <h2 className="cp-visually-hidden">{title}</h2>
      )}
      {children}
    </section>
  );
}

export function RecordDetailCard({ children }: { children: React.ReactNode }) {
  return <dl className="cp-detail-card">{children}</dl>;
}

export function RecordFieldRow({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="cp-field-row">
      <dt>
        {label}
        {required ? (
          <span className="cp-required-marker" aria-hidden="true">
            *
          </span>
        ) : null}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}
